// 3rd party libs
import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useHistory, Prompt } from 'react-router-dom';

// Components
import AlertIcon from './AlertIcon';
import { Button, Modal, Typography } from 'components';

// Types
import { Location } from 'history';
import { ModalSize } from 'types';
import theme from 'themes';

interface Props {
  helperText: string;
  type?: 'info' | 'warning' | 'error';
  isConfirmPrompt?: boolean;
  isVisible: boolean;
  onCancelCallback?: () => void;
  onConfirmCallback?: () => void;
  title: string;
}

// Styled Components
const StyledModalHeaderContainer = styled.div`
  align-items: center;
  display: flex;
  gap: 1.25rem;
  padding: 1.25rem 1.5rem;
  width: 10 0%;
`;

const StyledButtonRow = styled.div<{ type?: Props['type'] }>`
  align-item: center;
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
  padding-top: 0.5rem;
  color: ${({ type }) =>
    type === 'info'
      ? theme.colors.primaryBlue5
      : type === 'error'
      ? theme.colors.error200
      : type === 'warning'
      ? theme.colors.warning
      : theme.colors.error200};
  font-family: Roboto;
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
`;

const StyledTitle = styled.div`
  font-size: 1.125rem;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0rem;
  line-height: 1.3125rem;
`;

const StyledContentContainer = styled.div`
  padding: 0 3.9rem 1.25rem;
  width: 100%;
`;

interface Props {
  helperText: string;
  type?: 'info' | 'warning' | 'error';
  isConfirmPrompt?: boolean;
  isVisible: boolean;
  onCancelCallback?: () => void;
  onConfirmCallback?: () => void;
  title: string;
}

const WarningPrompt = ({
  helperText,
  isConfirmPrompt,
  isVisible,
  onCancelCallback,
  onConfirmCallback,
  type = 'error',
  title
}: Props): JSX.Element => {
  const history = useHistory();
  const theme = useTheme();

  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [lastLocation, setLastLocation] = useState<Location | null>(null);

  const fillerColor =
    type === 'info'
      ? theme.colors.primaryBlue5
      : type === 'warning'
      ? theme.colors.warning
      : type === 'error'
      ? theme.colors.error200
      : theme.colors.error200;

  const backgroundFillerColor =
    type === 'info'
      ? theme.colors.info300
      : type === 'warning'
      ? theme.colors.warning300
      : type === 'error'
      ? theme.colors.error300
      : theme.colors.error300;

  useEffect(() => {
    isVisible && setIsConfirmed(false);
  }, [isVisible]);

  const handleBlocked = (nextLocation?: Location) => {
    if (!isConfirmed && isVisible) {
      setIsModalOpen(true);
      nextLocation && setLastLocation(nextLocation);
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (isConfirmPrompt && isVisible) {
      handleBlocked();
    }
  }, [isConfirmPrompt, isVisible]);

  const handleConfirm = () => {
    setIsModalOpen(false);
    setIsConfirmed(true);
  };

  useEffect(() => {
    if (isConfirmed) {
      onConfirmCallback?.();
      lastLocation && history.push(lastLocation.pathname);
      setIsConfirmed(false);
    }
  }, [isConfirmed, lastLocation]);

  return (
    <>
      <Prompt message={handleBlocked} when={isVisible} />
      <Modal
        allowContentScroll
        onClose={() => {
          setIsModalOpen(false);
          onCancelCallback?.();
        }}
        showCloseButton={true}
        size={ModalSize.XXSMALL_AUTO_HEIGHT}
        widthOverride={'22rem'}
        showVanityBorder={true}
        vanityBorderColor={fillerColor}
        hasBackgroundColor={true}
        backgroundColor={backgroundFillerColor}
        customCloseButtonColor={fillerColor}
        title={
          <StyledModalHeaderContainer>
            <AlertIcon type={type} />
            <StyledTitle>{title}</StyledTitle>
          </StyledModalHeaderContainer>
        }
        visible={isModalOpen}
      >
        <StyledContentContainer>
          <Typography color={theme.colors.text.lightBlack} variant="stepheading">
            {helperText}
          </Typography>
          <StyledButtonRow type={type}>
            <Button
              onClick={() => {
                setIsModalOpen(false);
                onCancelCallback?.();
              }}
              variant="text"
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} variant="text">
              Yes
            </Button>
          </StyledButtonRow>
        </StyledContentContainer>
      </Modal>
    </>
  );
};

export default WarningPrompt;
